import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { BigFiveAnswer, BigFiveScores, PersonalityProfileDto } from '@llura/types';

// TODO(auth): re-import { api } from '../lib/api-client' and
// { syncFireAndForget } from '../lib/sync' once /me/* endpoints are guarded
// by a real session — then re-add the server sync in setSkipped() and
// retake() (see the matching TODO markers below).

interface PersonalityState {
  /**
   * One-shot routing gate. True once the user has either submitted answers
   * OR explicitly skipped — either way, we never bounce them back into the
   * test on later visits.
   */
  seen: boolean;
  scores: BigFiveScores | null;
  narrative: string | null;
  narrativeLocale: string | null;
  /**
   * The 10 raw Likert answers, kept so they can be carried into /me/claim at
   * signup (the server column is otherwise write-once at submit time). Null when
   * skipped or hydrated from the server bundle, which doesn't return them.
   */
  rawAnswers: BigFiveAnswer[] | null;

  /** Submit the 10 answers, persist the resulting profile + narrative. */
  setResult: (input: {
    scores: BigFiveScores;
    narrative: string;
    narrativeLocale: string;
    rawAnswers: BigFiveAnswer[];
  }) => void;
  /** Mark seen without taking the test. */
  setSkipped: () => void;
  /**
   * "Retake the test" from the Your-personality screen: wipe the local
   * snapshot and ask the server to delete the row + reopen the routing gate.
   * Caller then navigates to /personality-test.
   */
  retake: () => void;
  /** Overwrite from the server bundle on boot. */
  hydrate: (input: {
    seen: boolean;
    profile: PersonalityProfileDto | null;
  }) => void;
  reset: () => void;
}

const initial = {
  seen: false,
  scores: null,
  narrative: null,
  narrativeLocale: null,
  rawAnswers: null,
};

export const usePersonalityStore = create<PersonalityState>()(
  persist(
    (set) => ({
      ...initial,
      setResult: ({ scores, narrative, narrativeLocale, rawAnswers }) => {
        set({ seen: true, scores, narrative, narrativeLocale, rawAnswers });
        // The submit API call is owned by the page (it needs the result for
        // the next-screen render); this store just persists what came back.
      },
      setSkipped: () => {
        set({ seen: true, scores: null, narrative: null, narrativeLocale: null });
        // TODO(auth): syncFireAndForget(() => api.me.skipPersonality())
      },
      retake: () => {
        set({ ...initial });
        // TODO(auth): syncFireAndForget(() => api.me.resetPersonality())
      },
      hydrate: ({ seen, profile }) =>
        set({
          seen,
          scores: profile?.scores ?? null,
          narrative: profile?.narrative ?? null,
          narrativeLocale: profile?.narrativeLocale ?? null,
          // Server bundle doesn't carry raw answers; the profile is already
          // persisted, so a claim isn't needed once we're hydrated from it.
          rawAnswers: null,
        }),
      reset: () => set({ ...initial }),
    }),
    {
      name: 'llura.personality',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
